import { apiGet, apiPost, apiPut, getBootstrap, type RequestOptions } from './index'

export interface WeatherRateLimit {
  count: number
  limit: number
  resets_at: string | null
}

export interface WeatherSourceMetadata {
  endpoint: string | null
  assistant_endpoint: string | null
  prompt: string | null
  location_input: string | null
  units: string | null
  lat: number | null
  lon: number | null
  timezone: string | null
  timezone_offset: number | null
  http_status: number | null
  session_id: string | null
  resolved_location: string | null
  data_source: string | null
  data_version: string | null
  units_label: string | null
  answer_length: number | null
  data_keys: string[]
}

export interface WeatherAssistantSessionMetadata {
  id?: string | null
  created_at?: string | null
  started_at?: string | null
  refreshed_at?: string | null
  finished_at?: string | null
  expires_at?: string | null
  request_id?: string | null
  model?: string | null
  finish_reason?: string | null
  cached?: boolean | null
  cached_reason?: string | null
  http_status?: number | null
  prompt_tokens?: number | null
  completion_tokens?: number | null
  total_tokens?: number | null
  duration_ms?: number | null
  [key: string]: string | number | boolean | null | undefined
}

export interface WeatherSummary {
  temperature: number | null
  feels_like: number | null
  temperature_unit: string | null
  humidity: number | null
  pressure: number | null
  dew_point: number | null
  uvi: number | null
  visibility: number | null
  wind_speed: number | null
  wind_gust: number | null
  wind_deg: number | null
  sunrise: string | null
  sunset: string | null
  description: string | null
  condition: string | null
  icon: string | null
  observed_at: string | null
  timezone: string | null
}

export interface WeatherHourlyEntry {
  time: string | null
  temperature: number | null
  feels_like: number | null
  pop: number | null
  humidity: number | null
  wind_speed: number | null
  wind_deg: number | null
  description: string | null
  icon: string | null
  units: string | null
}

export interface WeatherDailyEntry {
  date: string | null
  temp_min: number | null
  temp_max: number | null
  temp_day: number | null
  temp_night: number | null
  pop: number | null
  sunrise: string | null
  sunset: string | null
  description: string | null
  icon: string | null
  units: string | null
}

export interface WeatherAlert {
  event: string | null
  description: string | null
  start: string | null
  end: string | null
  sender: string | null
  tags: string[] | null
  severity: string | null
}

export interface WeatherReportPayload {
  summary: WeatherSummary
  hourly?: WeatherHourlyEntry[] | null
  daily?: WeatherDailyEntry[] | null
  alerts: WeatherAlert[]
  fetched_at: string
  valid_until: string
  manual_cooldown: WeatherManualCooldown
  source_metadata: WeatherSourceMetadata
  assistant_answer: string | null
  assistant_session?: WeatherAssistantSessionMetadata | null
}

export interface WeatherManualCooldown {
  seconds: number
  expires_at: string | null
}

export interface WeatherCooldown extends WeatherManualCooldown {
  remaining: number
}

export interface WeatherReport {
  id: number
  status: string
  refresh_type: string
  payload_hash: string
  payload: WeatherReportPayload | null
  fetched_at: string | null
  valid_until: string | null
  created_at: string | null
  updated_at: string | null
  error_code: string | null
  error_message: string | null
}

export type WeatherStaleReason = 'disabled' | 'missing' | 'last_error' | 'expired' | string | null

export interface WeatherStaleState {
  is_stale: boolean
  reason: WeatherStaleReason
}

export interface WeatherState {
  station_id: number
  enabled: boolean
  location: string
  units: string
  status: string
  last_updated: string | null
  cooldown: WeatherCooldown
  manual_refresh: {
    presenter_enabled: boolean
    allowed_for_user: boolean
    last_manual_request_at: string | null
  }
  rate_limits: Record<string, WeatherRateLimit>
  report: WeatherReport | null
  stale: WeatherStaleState
}

export type WeatherManualRefreshStatus = 'success' | 'cooldown' | 'error'

export interface WeatherManualRefreshResponse {
  status: WeatherManualRefreshStatus
  message: string
  error_code: string | null
  next_allowed_at: string | null
  cooldown: WeatherCooldown
  rate_limits: Record<string, WeatherRateLimit>
  weather: WeatherState
}

export interface UpdateStationWeatherPayload {
  weather_enabled?: boolean
  weather_location?: string
  weather_units?: string
  presenter_manual_refresh_enabled?: boolean
  manual_cooldown_minutes?: number
}

export interface GlobalWeatherCronMode {
  type: 'interval_minutes' | 'specific_hours'
  interval_minutes: number
  specific_hours: number[]
}

export interface WeatherFailureEntry {
  error_code: string | null
  error_message: string | null
  refresh_type: string
  occurred_at: string | null
}

export interface WeatherFailureSummary {
  consecutive_failures: number
  threshold: number
  is_warning: boolean
  last_failure: WeatherFailureEntry | null
  last_success_at: string | null
  recent_failures: WeatherFailureEntry[]
}

export interface SuperadminWeatherRateLimit extends WeatherRateLimit {
  warning_threshold: number
  warning_ratio: number
  is_warning: boolean
}

export interface WeatherReportListEntry {
  id: number
  station_id: number
  station_name: string
  created_at: string | null
  status: string
  http_status: number | null
  refresh_type: string
  location_input: string
  duration_ms: number | null
  error_message: string | null
}

export interface WeatherReportListResponse {
  data: WeatherReportListEntry[]
  paging: {
    page: number
    per_page: number
    total: number
    has_next: boolean
  }
  filters: {
    station_id: number | null
    status: 'all' | 'success' | 'error'
    range: '24h' | '48h' | '7d' | null
    start: string | null
    end: string | null
    refresh_type: string | null
  }
}

export interface WeatherReportListFilters {
  station_id?: number | null
  status?: 'all' | 'success' | 'error'
  range?: '24h' | '48h' | '7d'
  start?: string | null
  end?: string | null
  refresh_type?: string | null
  page?: number
  per_page?: number
}

export type WeatherReportRawPayload = Record<string, unknown>

export interface SuperadminWeatherSettingsResponse {
  station_id: number
  api_key: string
  manual_cooldown_minutes: number
  cron_mode: GlobalWeatherCronMode
  presenter_manual_refresh_enabled: boolean
  rate_limits: Record<string, SuperadminWeatherRateLimit>
  failure_summary: WeatherFailureSummary
  reset?: {
    rate_limits: boolean
  }
}

export interface SaveSuperadminWeatherSettingsPayload {
  api_key?: string | null
  manual_cooldown_minutes?: number
  cron_mode?: Partial<GlobalWeatherCronMode>
  presenter_manual_refresh_enabled?: boolean
}

export interface ResetWeatherCountersPayload {
  reset_rate_limits?: boolean
}

export async function fetchWeather(
  stationId: number,
  options: RequestOptions = {},
): Promise<WeatherState> {
  const params = new URLSearchParams({ station_id: String(stationId) })
  return apiGet<WeatherState>(`weather?${params.toString()}`, options)
}

export async function refreshWeather(
  stationId: number,
  options: RequestOptions = {},
): Promise<WeatherManualRefreshResponse> {
  return apiPost<WeatherManualRefreshResponse>('weather/refresh', { station_id: stationId }, options)
}

export async function updateStationWeather(
  stationId: number,
  payload: UpdateStationWeatherPayload,
  options: RequestOptions = {},
): Promise<WeatherState> {
  return apiPut<WeatherState>(`stations/${stationId}/weather`, payload, options)
}

export async function getSuperadminWeatherSettings(
  stationId: number,
  options: RequestOptions = {},
): Promise<SuperadminWeatherSettingsResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })
  return apiGet<SuperadminWeatherSettingsResponse>(
    `superadmin/weather-settings?${params.toString()}`,
    options,
  )
}

export async function saveSuperadminWeatherSettings(
  stationId: number,
  payload: SaveSuperadminWeatherSettingsPayload,
  options: RequestOptions = {},
): Promise<SuperadminWeatherSettingsResponse> {
  return apiPut<SuperadminWeatherSettingsResponse>(
    'superadmin/weather-settings',
    { station_id: stationId, ...payload },
    options,
  )
}

export async function resetSuperadminWeatherCounters(
  stationId: number,
  payload: ResetWeatherCountersPayload = {},
  options: RequestOptions = {},
): Promise<SuperadminWeatherSettingsResponse> {
  return apiPost<SuperadminWeatherSettingsResponse>(
    'superadmin/weather-settings/reset',
    { station_id: stationId, ...payload },
    options,
  )
}

export async function getSuperadminWeatherReports(
  filters: WeatherReportListFilters = {},
  options: RequestOptions = {},
): Promise<WeatherReportListResponse> {
  const params = new URLSearchParams()

  if (typeof filters.station_id === 'number') {
    params.set('station_id', String(filters.station_id))
  }

  if (filters.status) {
    params.set('status', filters.status)
  }

  if (filters.range) {
    params.set('range', filters.range)
  }

  if (filters.start) {
    params.set('start', filters.start)
  }

  if (filters.end) {
    params.set('end', filters.end)
  }

  if (filters.refresh_type) {
    params.set('refresh_type', filters.refresh_type)
  }

  if (typeof filters.page === 'number') {
    params.set('page', String(filters.page))
  }

  if (typeof filters.per_page === 'number') {
    params.set('per_page', String(filters.per_page))
  }

  const path =
    params.toString().length > 0
      ? `superadmin/weather-reports?${params.toString()}`
      : 'superadmin/weather-reports'

  return apiGet<WeatherReportListResponse>(path, options)
}

export async function getSuperadminWeatherReportRaw(
  reportId: number,
  options: RequestOptions = {},
): Promise<WeatherReportRawPayload> {
  return apiGet<WeatherReportRawPayload>(`superadmin/weather-reports/${reportId}/raw`, options)
}

export function getSuperadminWeatherReportRawUrl(reportId: number, download = false): string {
  const { restBase, nonce } = getBootstrap()
  const url = new URL(`superadmin/weather-reports/${reportId}/raw`, restBase)

  if (download) {
    url.searchParams.set('download', '1')
  }

  url.searchParams.set('_wpnonce', nonce)

  return url.toString()
}
